// src/features/dashboard/components/EmployeeProductivity/TimeClassificationChart.tsx

import {
  useMemo,
  useState,
} from "react";

import {
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
} from "recharts";

import {
  TimeClassificationItem,
} from "../../types/Employee/employeeProductivity.types";

import {
  PRODUCTIVE_APP_COLORS,
  WORKVISTA_UI_COLORS,
} from "../../constants/chartColors";

/* ==========================================================================
 * PROPS
 * ========================================================================== */

interface Props {
  data: TimeClassificationItem[];
}

/* ==========================================================================
 * TYPES
 * ========================================================================== */

interface ChartItem
  extends TimeClassificationItem {
  chartColor: string;
}

interface HoverState {
  index: number | null;
  x: number;
  y: number;
  side:
    | "left"
    | "right"
    | "top"
    | "bottom";
}

/* ==========================================================================
 * CONSTANTS
 * ========================================================================== */

const RADIAN =
  Math.PI / 180;

/* ==========================================================================
 * TIME HELPERS
 * ========================================================================== */

const timeToSeconds = (
  time?: string,
): number => {
  if (!time) {
    return 0;
  }

  const [
    hours = 0,
    minutes = 0,
    seconds = 0,
  ] = time
    .split(":")
    .map(Number);

  return (
    hours * 3600 +
    minutes * 60 +
    seconds
  );
};

/* --------------------------------------------------------------------------
 * Seconds → HH h MM m
 * -------------------------------------------------------------------------- */

const secondsToHoursMinutes = (
  seconds: number,
): string => {
  const safeSeconds =
    Math.max(
      0,
      Math.floor(seconds),
    );

  const hours =
    Math.floor(
      safeSeconds / 3600,
    );

  const minutes =
    Math.floor(
      (safeSeconds % 3600) /
        60,
    );

  return `${String(
    hours,
  ).padStart(
    2,
    "0",
  )} h ${String(
    minutes,
  ).padStart(
    2,
    "0",
  )} m`;
};

/* --------------------------------------------------------------------------
 * API duration → HH h MM m
 * -------------------------------------------------------------------------- */

const formatDuration = (
  time?: string,
): string => {
  if (!time) {
    return "00 h 00 m";
  }

  const [
    hours = "00",
    minutes = "00",
  ] = time.split(":");

  return `${String(
    Number(hours),
  ).padStart(
    2,
    "0",
  )} h ${String(
    Number(minutes),
  ).padStart(
    2,
    "0",
  )} m`;
};

/* ==========================================================================
 * COLOR MAPPING
 * ========================================================================== */

/**
 * IMPORTANT:
 *
 * Do NOT use CHART_COLORS_500 for this chart.
 *
 * CHART_COLORS_500 starts with the maroon/red palette,
 * which is why the old screenshot was showing:
 *
 * No-Impact -> maroon
 *
 * Instead this chart uses semantic WorkVista colors.
 *
 * No-Impact:
 *   WORKVISTA_UI_COLORS.previousSeries
 *
 * Productive:
 *   PRODUCTIVE_APP_COLORS[1] -> Royal Blue
 *
 * This keeps the chart clean and consistent.
 */

const getClassificationColor = (
  name?: string,
  index = 0,
): string => {
  const normalizedName =
    String(name ?? "")
      .trim()
      .toLowerCase();

  /* ------------------------------------------------------------------------
   * NO-IMPACT
   * ------------------------------------------------------------------------ */

  if (
    normalizedName ===
    "no-impact"
  ) {
    return (
      WORKVISTA_UI_COLORS
        .previousSeries.code
    );
  }

  /* ------------------------------------------------------------------------
   * PRODUCTIVE
   * ------------------------------------------------------------------------ */

  if (
    normalizedName ===
    "productive"
  ) {
    return (
      PRODUCTIVE_APP_COLORS[1]
        ?.code ??
      WORKVISTA_UI_COLORS
        .primary.code
    );
  }

  /* ------------------------------------------------------------------------
   * OTHER CATEGORIES
   *
   * If another category is added later,
   * use the centralized blue palette.
   * ------------------------------------------------------------------------ */

  return (
    PRODUCTIVE_APP_COLORS[
      (index + 2) %
        PRODUCTIVE_APP_COLORS.length
    ]?.code ??
    WORKVISTA_UI_COLORS
      .primary.code
  );
};

/* ==========================================================================
 * PIE LABEL
 * ========================================================================== */

const renderCustomizedLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
  value,
}: any) => {
  /**
   * Don't show text on very small slices.
   */
  if (
    typeof percent !==
      "number" ||
    percent < 0.04
  ) {
    return null;
  }

  const radius =
    innerRadius +
    (outerRadius -
      innerRadius) *
      0.5;

  const x =
    cx +
    radius *
      Math.cos(
        -midAngle *
          RADIAN,
      );

  const y =
    cy +
    radius *
      Math.sin(
        -midAngle *
          RADIAN,
      );

  return (
    <text
      x={x}
      y={y}
      fill={
        WORKVISTA_UI_COLORS
          .white.code
      }
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={9}
      fontWeight={700}
      pointerEvents="none"
    >
      {value}%
    </text>
  );
};

/* ==========================================================================
 * COMPONENT
 * ========================================================================== */

const TimeClassificationChart = ({
  data,
}: Props) => {
  /* ------------------------------------------------------------------------
   * HOVER STATE
   * ------------------------------------------------------------------------ */

  const [
    hovered,
    setHovered,
  ] = useState<HoverState>({
    index: null,
    x: 0,
    y: 0,
    side: "right",
  });

  /* ------------------------------------------------------------------------
   * CHART DATA
   * ------------------------------------------------------------------------ */

  const chartData =
    useMemo<ChartItem[]>(() => {
      if (
        !Array.isArray(data)
      ) {
        return [];
      }

      return data.map(
        (
          item,
          index,
        ) => ({
          ...item,

          /**
           * IMPORTANT:
           *
           * The color is determined by
           * the category name.
           *
           * This means:
           *
           * No-Impact = gray
           * Productive = blue
           *
           * and it does NOT depend on the
           * order of the API response.
           */
          chartColor:
            getClassificationColor(
              item.name,
              index,
            ),
        }),
      );
    }, [data]);

  /* ------------------------------------------------------------------------
   * TOTAL ACTIVE TIME
   * ------------------------------------------------------------------------ */

  const totalActiveTime =
    useMemo(() => {
      const totalSeconds =
        chartData.reduce(
          (
            sum,
            item,
          ) =>
            sum +
            timeToSeconds(
              item.duration,
            ),
          0,
        );

      return secondsToHoursMinutes(
        totalSeconds,
      );
    }, [chartData]);

  /* ------------------------------------------------------------------------
   * ACTIVE ITEM
   * ------------------------------------------------------------------------ */

  const activeItem =
    hovered.index !== null
      ? chartData[
          hovered.index
        ]
      : chartData.find(
          (item) =>
            item.name
              ?.trim()
              .toLowerCase() ===
            "no-impact",
        ) ||
        chartData[0];

  /* ------------------------------------------------------------------------
   * HOVER HANDLER
   * ------------------------------------------------------------------------ */

  const handleSliceEnter = (
    entry: any,
    index: number,
  ) => {
    const cx =
      Number(
        entry?.cx,
      ) || 117;

    const cy =
      Number(
        entry?.cy,
      ) || 107;

    const outerRadius =
      Number(
        entry?.outerRadius,
      ) || 90;

    const midAngle =
      Number(
        entry?.midAngle,
      ) || 0;

    /**
     * Keep the card close to
     * the hovered slice.
     */
    const tooltipRadius =
      outerRadius + 12;

    const radians =
      -midAngle * RADIAN;

    const rawX =
      cx +
      tooltipRadius *
        Math.cos(
          radians,
        );

    const rawY =
      cy +
      tooltipRadius *
        Math.sin(
          radians,
        );

    const horizontal =
      Math.cos(
        radians,
      );

    const vertical =
      Math.sin(
        radians,
      );

    let side:
      | "left"
      | "right"
      | "top"
      | "bottom" =
      "right";

    if (
      Math.abs(
        horizontal,
      ) >
      Math.abs(vertical)
    ) {
      side =
        horizontal >= 0
          ? "right"
          : "left";
    } else {
      side =
        vertical >= 0
          ? "bottom"
          : "top";
    }

    /**
     * Keep tooltip within the
     * chart wrapper.
     */
    const x =
      Math.max(
        18,
        Math.min(
          rawX,
          217,
        ),
      );

    const y =
      Math.max(
        32,
        Math.min(
          rawY,
          183,
        ),
      );

    setHovered({
      index,
      x,
      y,
      side,
    });
  };

  /* ------------------------------------------------------------------------
   * CLEAR HOVER
   * ------------------------------------------------------------------------ */

  const clearHover = () => {
    setHovered({
      index: null,
      x: 0,
      y: 0,
      side: "right",
    });
  };

  /* ------------------------------------------------------------------------
   * EMPTY
   * ------------------------------------------------------------------------ */

  if (
    chartData.length === 0
  ) {
    return (
      <div
        className="
          rounded-[16px]
          border
          border-[#D0D5DD]
          bg-white
          px-5
          pt-4
          pb-4
        "
      >
        <div
          className="
            flex
            min-h-[260px]
            items-center
            justify-center
            text-[12px]
            text-[#667085]
          "
        >
          No time classification
          data available
        </div>
      </div>
    );
  }

  /* ------------------------------------------------------------------------
   * RENDER
   * ------------------------------------------------------------------------ */

  return (
    <div
      className="
        relative
        w-full
        min-w-0
        overflow-visible
        rounded-[16px]
        border
        border-[#D0D5DD]
        bg-white
        px-5
        pt-4
        pb-4
      "
    >
      {/* ================================================================
       * HEADER
       * ================================================================ */}

      <div
        className="
          mb-2
          flex
          items-start
          justify-between
          gap-3
        "
      >
        <h3
          className="
            leading-[20px]
            text-[14px]
            font-semibold
            text-[#002B5B]
          "
        >
          Time
          <br />
          Classification
        </h3>

        <div
          className="
            rounded-full
            bg-[#EEF4FF]
            px-4
            py-[4px]
            text-[11px]
            leading-[12px]
            font-medium
            text-[#2563EB]
          "
        >
          Of {totalActiveTime} active
          time today
        </div>
      </div>

      {/* ================================================================
       * CHART
       * ================================================================ */}

      <div
        className="
          relative
          mx-auto
          mt-[-2px]
          h-[215px]
          w-[235px]
          overflow-visible
        "
      >
        <ResponsiveContainer
          width="100%"
          height="100%"
        >
          <PieChart
            margin={{
              top: 2,
              right: 2,
              bottom: 2,
              left: 2,
            }}
          >
            <Pie
              data={chartData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={56}
              outerRadius={90}
              startAngle={90}
              endAngle={-270}
              paddingAngle={2}
              cornerRadius={2}
              stroke={
                WORKVISTA_UI_COLORS
                  .white.code
              }
              strokeWidth={4}
              label={
                renderCustomizedLabel
              }
              labelLine={false}
              isAnimationActive={false}
              onMouseEnter={(
                entry: any,
                index: number,
              ) => {
                handleSliceEnter(
                  entry,
                  index,
                );
              }}
              onMouseLeave={
                clearHover
              }
            >
              {chartData.map(
                (
                  entry,
                  index,
                ) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={
                      entry.chartColor
                    }
                    fillOpacity={
                      hovered.index ===
                        null ||
                      hovered.index ===
                        index
                        ? 1
                        : 0.3
                    }
                    stroke={
                      WORKVISTA_UI_COLORS
                        .white.code
                    }
                    strokeWidth={4}
                    style={{
                      cursor:
                        "pointer",
                      transition:
                        "fill-opacity 180ms ease",
                    }}
                  />
                ),
              )}
            </Pie>
          </PieChart>
        </ResponsiveContainer>

        {/* ==============================================================
         * CENTER
         * ============================================================== */}

        <div
          className="
            pointer-events-none
            absolute
            inset-0
            flex
            flex-col
            items-center
            justify-center
          "
        >
          <p
            className="
              max-w-[110px]
              truncate
              text-[13px]
              font-medium
              text-[#1D2939]
            "
            title={
              activeItem?.name
            }
          >
            {activeItem?.name}
          </p>

          <p
            className="
              mt-1
              text-[18px]
              font-bold
              text-[#101828]
            "
          >
            {formatDuration(
              activeItem?.duration,
            )}
          </p>

          <p
            className="
              text-[16px]
              font-semibold
            "
            style={{
              color:
                activeItem?.chartColor ||
                WORKVISTA_UI_COLORS
                  .blueText.code,
            }}
          >
            {activeItem?.value}%
          </p>
        </div>

        {/* ==============================================================
         * HOVER CARD
         * ============================================================== */}

        {hovered.index !==
          null &&
          activeItem && (
            <div
              className="
                pointer-events-none
                absolute
                z-[999]
                w-[150px]
              "
              style={{
                left:
                  hovered.x,
                top:
                  hovered.y,
                transform:
                  hovered.side ===
                  "right"
                    ? "translate(0, -50%)"
                    : hovered.side ===
                        "left"
                      ? "translate(-100%, -50%)"
                      : "translate(-50%, -50%)",
              }}
            >
              <div
                className="
                  relative
                  rounded-[8px]
                  border
                  border-[#EAECF0]
                  bg-white
                  px-3
                  py-2
                  shadow-[0_4px_14px_rgba(16,24,40,0.16)]
                "
              >
                {/* TOP */}

                <div
                  className="
                    flex
                    min-w-0
                    items-center
                    gap-2
                  "
                >
                  {/* COLOR DOT */}

                  <span
                    className="
                      h-[9px]
                      w-[9px]
                      shrink-0
                      rounded-full
                    "
                    style={{
                      backgroundColor:
                        activeItem.chartColor,
                    }}
                  />

                  {/* NAME */}

                  <span
                    className="
                      min-w-0
                      flex-1
                      truncate
                      text-[11px]
                      font-medium
                      text-[#344054]
                    "
                    title={
                      activeItem.name
                    }
                  >
                    {
                      activeItem.name
                    }
                  </span>

                  {/* PERCENT */}

                  <span
                    className="
                      shrink-0
                      text-[11px]
                      font-semibold
                    "
                    style={{
                      color:
                        activeItem.chartColor,
                    }}
                  >
                    {
                      activeItem.value
                    }
                    %
                  </span>
                </div>

                {/* DURATION */}

                <div
                  className="
                    mt-1
                    pl-[17px]
                    text-[10px]
                    font-medium
                    text-[#667085]
                  "
                >
                  {formatDuration(
                    activeItem.duration,
                  )}
                </div>

                {/* ========================================================
                 * ARROW
                 * ======================================================== */}

                {hovered.side ===
                  "right" && (
                  <span
                    className="
                      absolute
                      left-[-5px]
                      top-1/2
                      h-[9px]
                      w-[9px]
                      -translate-y-1/2
                      rotate-45
                      border-b
                      border-l
                      border-[#EAECF0]
                      bg-white
                    "
                  />
                )}

                {hovered.side ===
                  "left" && (
                  <span
                    className="
                      absolute
                      right-[-5px]
                      top-1/2
                      h-[9px]
                      w-[9px]
                      -translate-y-1/2
                      rotate-45
                      border-t
                      border-r
                      border-[#EAECF0]
                      bg-white
                    "
                  />
                )}

                {hovered.side ===
                  "top" && (
                  <span
                    className="
                      absolute
                      bottom-[-5px]
                      left-1/2
                      h-[9px]
                      w-[9px]
                      -translate-x-1/2
                      rotate-45
                      border-r
                      border-b
                      border-[#EAECF0]
                      bg-white
                    "
                  />
                )}

                {hovered.side ===
                  "bottom" && (
                  <span
                    className="
                      absolute
                      left-1/2
                      top-[-5px]
                      h-[9px]
                      w-[9px]
                      -translate-x-1/2
                      rotate-45
                      border-l
                      border-t
                      border-[#EAECF0]
                      bg-white
                    "
                  />
                )}
              </div>
            </div>
          )}
      </div>

      {/* ================================================================
       * LEGEND
       * ================================================================ */}

      <div
        className="
          mt-1
          flex
          flex-wrap
          items-center
          justify-center
          gap-x-5
          gap-y-2
        "
      >
        {chartData.map(
          (
            item,
            index,
          ) => {
            const isActive =
              hovered.index ===
              index;

            return (
              <div
                key={`${item.name}-${index}`}
                className="
                  flex
                  cursor-pointer
                  items-center
                  gap-1.5
                  transition-all
                  duration-200
                "
                onMouseEnter={() => {
                  setHovered({
                    index,
                    x:
                      index ===
                      0
                        ? 25
                        : 210,
                    y: 105,
                    side:
                      index ===
                      0
                        ? "right"
                        : "left",
                  });
                }}
                onMouseLeave={
                  clearHover
                }
              >
                {/* COLOR */}

                <span
                  className="
                    h-[10px]
                    w-[10px]
                    shrink-0
                    rounded-full
                    transition-all
                    duration-200
                  "
                  style={{
                    backgroundColor:
                      item.chartColor,
                    opacity:
                      hovered.index ===
                        null ||
                      isActive
                        ? 1
                        : 0.3,
                    transform:
                      isActive
                        ? "scale(1.15)"
                        : "scale(1)",
                  }}
                />

                {/* NAME */}

                <span
                  className={`
                    text-[11px]
                    transition-all
                    duration-200
                    ${
                      isActive
                        ? "font-semibold text-[#101828]"
                        : "font-medium text-[#344054]"
                    }
                  `}
                >
                  {item.name}
                </span>
              </div>
            );
          },
        )}
      </div>
    </div>
  );
};

export default TimeClassificationChart;